import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Alert,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Autocomplete
} from '@mui/material';
import { CloudUpload, CheckCircle, Error, Description as DescriptionIcon, Edit as EditIcon } from '@mui/icons-material';
import { TiptapEditor } from './TiptapEditor';
import { lessonPlanService } from '../services/lessonPlanService';
import { subjectService } from '../services/subjectService';
import type { LessonPlan, LessonPlanFormData, LessonPlanFolder, Subject } from '../types';
import { FolderSelector } from './FolderSelector';
import { logger } from '../utils/logger';
import { useAppStore } from '../stores/useAppStore';
import { isAdminOrApprover } from '../utils/rbac';
import { getDropzoneAcceptConfig, MAX_FILE_SIZE, FILE_VALIDATION_ERRORS } from '../utils/fileValidation';

interface LessonPlanEditProps {
  lessonPlan: LessonPlan;
  onEditSuccess?: (lessonPlan: any) => void;
  onEditError?: (error: string) => void;
  onClose?: () => void;
  folders?: LessonPlanFolder[];
}

export const LessonPlanEdit: React.FC<LessonPlanEditProps> = ({
  lessonPlan,
  onEditSuccess,
  onEditError,
  onClose,
  folders = []
}) => {
  const { user } = useAppStore();
  const userRoles = user?.roles || [];
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState(lessonPlan.title || '');
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [subjectSearchTerm, setSubjectSearchTerm] = useState('');
  const [week, setWeek] = useState<number | ''>(lessonPlan.week || '');
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(lessonPlan.folderId || null);
  const [submitImmediately, setSubmitImmediately] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submissionPeriod, setSubmissionPeriod] = useState<{ startDate: string; endDate: string } | null>(null);
  const [overrideConfirmOpen, setOverrideConfirmOpen] = useState(false);
  const [overrideConfirmed, setOverrideConfirmed] = useState(false);

  // Digital Content State
  const [digitalContent, setDigitalContent] = useState(lessonPlan.content || '');
  const isDigital = lessonPlan.is_digital || false;

  // Load subjects for autocomplete
  const loadSubjects = async (search?: string): Promise<Subject[]> => {
    try {
      setLoadingSubjects(true);
      const result = await subjectService.getSubjects({
        page: 1,
        pageSize: 1000,
        search: search || ''
      });
      const loadedSubjects = result.subjects || [];
      setSubjects(loadedSubjects);
      return loadedSubjects;
    } catch (err) {
      logger.error('Error loading subjects:', err);
      return [];
    } finally {
      setLoadingSubjects(false);
    }
  };

  // Load subjects and set initial subject
  useEffect(() => {
    const loadInitialData = async () => {
      const loadedSubjects = await loadSubjects();
      if (lessonPlan.subjectId) {
        // Load subject by ID
        try {
          const subject = await subjectService.getSubjectById(lessonPlan.subjectId);
          setSelectedSubject(subject);
          setSubjectSearchTerm(subject.name);
        } catch (err) {
          logger.error('Error loading subject:', err);
          // Fallback to subject name
          if (lessonPlan.subject) {
            setSubjectSearchTerm(lessonPlan.subject);
          }
        }
      } else if (lessonPlan.subject) {
        // Fallback: try to find subject by name
        const found = loadedSubjects.find(s => s.name === lessonPlan.subject);
        if (found) {
          setSelectedSubject(found);
          setSubjectSearchTerm(found.name);
        } else {
          // Try searching with subject name
          const searchResults = await loadSubjects(lessonPlan.subject);
          const foundInSearch = searchResults.find(s => s.name === lessonPlan.subject);
          if (foundInSearch) {
            setSelectedSubject(foundInSearch);
            setSubjectSearchTerm(foundInSearch.name);
          } else {
            setSubjectSearchTerm(lessonPlan.subject);
          }
        }
      }
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonPlan.id]);

  // Debounce subject search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (subjectSearchTerm && !selectedSubject) {
        loadSubjects(subjectSearchTerm);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [subjectSearchTerm, selectedSubject]);

  // Load submission period
  useEffect(() => {
    lessonPlanService.getSubmissionPeriod().then(period => {
      if (period) {
        setSubmissionPeriod(period);
      }
    }).catch(err => {
      logger.error('Error loading submission period:', err);
    });
  }, []);

  const isOutsideSubmissionPeriod = useCallback((): boolean => {
    if (!submissionPeriod?.startDate || !submissionPeriod?.endDate) return false;
    const start = new Date(submissionPeriod.startDate);
    const end = new Date(submissionPeriod.endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;
    const now = new Date();
    return now < start || now > end;
  }, [submissionPeriod]);

  const formatSubmissionPeriodLine = useCallback((): string => {
    if (!submissionPeriod?.startDate || !submissionPeriod?.endDate) return '';
    const start = new Date(submissionPeriod.startDate);
    const end = new Date(submissionPeriod.endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return '';
    const startStr = start.toLocaleString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    const endStr = end.toLocaleString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    return `Thời gian nộp giáo án: Từ ${startStr} đến ${endStr}`;
  }, [submissionPeriod]);

  const handleFileSelect = useCallback((selectedFile: File) => {
    const validation = lessonPlanService.validateFile(selectedFile);
    if (!validation.isValid) {
      setError(validation.error || 'File không hợp lệ');
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setError(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        setError(`File không hợp lệ. ${FILE_VALIDATION_ERRORS.INVALID_TYPE}`);
        return;
      }
      if (acceptedFiles.length > 0) {
        handleFileSelect(acceptedFiles[0]);
      }
    },
    accept: getDropzoneAcceptConfig(),
    maxFiles: 1,
    maxSize: MAX_FILE_SIZE
  });

  const doSubmit = async () => {
    setIsUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      const formData: LessonPlanFormData = {
        title: title.trim(),
        subjectId: selectedSubject?.id || null,
        week: week !== '' ? Number(week) : undefined,
        file: !isDigital ? (file || (undefined as any)) : undefined, // File only for non-digital
        folderId: selectedFolderId || undefined,
        submitImmediately,
        content: isDigital ? digitalContent : undefined,
        is_digital: isDigital
      };

      const updatedLessonPlan = await lessonPlanService.updateLessonPlan(lessonPlan.id, formData);

      if (updatedLessonPlan?.status === 'PendingSubmit') {
        setSuccess('Đã lưu giáo án ở trạng thái "Chờ nộp". Hệ thống sẽ tự nộp khi tới thời gian nộp giáo án.');
      } else if (updatedLessonPlan?.status === 'Pending' && submitImmediately) {
        setSuccess('Đã nộp giáo án thành công!');
      } else {
        setSuccess('Cập nhật giáo án thành công!');
      }

      if (onEditSuccess) {
        onEditSuccess(updatedLessonPlan);
      }
    } catch (err) {
      logger.error('Error updating lesson plan:', err);
      const errorMessage = (err as Error)?.message || 'Có lỗi xảy ra khi cập nhật giáo án';
      setError(errorMessage);

      if (onEditError) {
        onEditError(errorMessage);
      }
    } finally {
      setIsUpdating(false);
      setOverrideConfirmed(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!title.trim()) {
      setError('Vui lòng nhập tiêu đề giáo án');
      return;
    }

    // Admin/BGH có thể nộp ngoài thời gian, nhưng phải confirm trước
    if (
      submitImmediately &&
      isAdminOrApprover(userRoles) &&
      submissionPeriod &&
      isOutsideSubmissionPeriod() &&
      !overrideConfirmed
    ) {
      setOverrideConfirmOpen(true);
      return;
    }

    await doSubmit();
  };

  return (
    <Box>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <CheckCircle sx={{ mr: 1 }} />
          {success}
        </Alert>
      )}

      {/* Override submission confirmation (Admin/BGH only, outside submission period) */}
      <Dialog
        open={overrideConfirmOpen}
        onClose={() => {
          if (!isUpdating) {
            setOverrideConfirmOpen(false);
            setOverrideConfirmed(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Xác nhận vượt quyền</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            Bạn đang vượt quyền để nộp giáo án. Bạn có chắc không?
          </DialogContentText>
          {!!formatSubmissionPeriodLine() && (
            <Typography variant="caption" color="text.secondary">
              {formatSubmissionPeriodLine()}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOverrideConfirmOpen(false);
              setOverrideConfirmed(false);
            }}
            disabled={isUpdating}
          >
            Hủy
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              setOverrideConfirmOpen(false);
              setOverrideConfirmed(true);
              doSubmit();
            }}
            disabled={isUpdating}
          >
            Đồng ý nộp
          </Button>
        </DialogActions>
      </Dialog>

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {isDigital ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <EditIcon sx={{ mr: 1, color: 'primary.main' }} />
                Nội dung giáo án (Soạn thảo trực tuyến)
              </Typography>
              <Box sx={{
                '& .ck-editor__editable': {
                  minHeight: '300px',
                  maxHeight: '500px'
                }
              }}>
                <TiptapEditor
                  content={digitalContent}
                  onChange={(content) => setDigitalContent(content)}
                  placeholder="Nhập nội dung giáo án..."
                />
              </Box>
            </Box>
          ) : (
            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed',
                borderColor: isDragReject ? 'error.main' : isDragActive ? 'primary.main' : '#ccc',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: isDragActive ? 'action.hover' : isDragReject ? 'error.lighter' : 'background.paper',
                transition: 'all 0.2s ease-in-out',
                minHeight: 150,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <input {...getInputProps()} id="file-input" />

              <CloudUpload
                sx={{
                  fontSize: 48,
                  color: isDragActive ? 'primary.main' : isDragReject ? 'error.main' : 'text.secondary',
                  mb: 2,
                  transition: 'all 0.2s ease-in-out',
                  transform: isDragActive ? 'scale(1.1)' : 'scale(1)'
                }}
              />

              <Typography variant="body1" gutterBottom sx={{ fontWeight: isDragActive ? 600 : 400 }}>
                {isDragActive
                  ? 'Thả file vào đây để thay thế...'
                  : isDragReject
                    ? 'File không hợp lệ! Chỉ chấp nhận file PDF, DOCX hoặc DOC'
                    : file
                      ? file.name
                      : lessonPlan.fileName || 'Kéo thả file vào đây hoặc click để chọn file mới (tùy chọn)'}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {isDragActive
                  ? 'File sẽ được thay thế ngay khi bạn thả'
                  : 'Hỗ trợ file PDF, DOCX, DOC (tối đa 50MB). Để trống nếu không muốn thay đổi file.'}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <TextField
              fullWidth
              label="Tiêu đề giáo án *"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              disabled={isUpdating}
            />

            <Autocomplete
              fullWidth
              options={subjects}
              getOptionLabel={(option) => option.name}
              value={selectedSubject}
              onChange={(_, newValue) => {
                setSelectedSubject(newValue);
                setSubjectSearchTerm(newValue?.name || '');
              }}
              onInputChange={(_, newInputValue) => {
                setSubjectSearchTerm(newInputValue);
              }}
              inputValue={subjectSearchTerm}
              loading={loadingSubjects}
              disabled={isUpdating}
              noOptionsText="Không tìm thấy môn học"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Môn học"
                  placeholder="Tìm kiếm môn học..."
                  helperText="Chọn môn học từ danh sách (không thể tạo mới)"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            <FormControl fullWidth disabled={isUpdating}>
              <InputLabel>Tuần</InputLabel>
              <Select
                value={week}
                onChange={(e) => setWeek(e.target.value as number | '')}
                label="Tuần"
              >
                <MenuItem value="">
                  <em>Không chọn</em>
                </MenuItem>
                {Array.from({ length: 40 }, (_, i) => i + 1).map((weekNum) => (
                  <MenuItem key={weekNum} value={weekNum}>
                    Tuần {weekNum}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {folders.length > 0 && (
            <FolderSelector
              folders={folders}
              value={selectedFolderId}
              onChange={setSelectedFolderId}
              label="Chọn folder (tùy chọn)"
              allowNone={true}
              noneLabel="Không có folder (folder gốc)"
            />
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={submitImmediately}
                onChange={(e) => setSubmitImmediately(e.target.checked)}
                disabled={isUpdating}
              />
            }
            label="Nộp luôn sau khi cập nhật (nếu không tick sẽ giữ nguyên trạng thái hiện tại)"
          />

          {submitImmediately && submissionPeriod && (
            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2">
                Thời gian nộp giáo án: Từ {new Date(submissionPeriod.startDate).toLocaleString('vi-VN', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })} đến {new Date(submissionPeriod.endDate).toLocaleString('vi-VN', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={isUpdating}
            >
              Hủy
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={!title.trim() || isUpdating}
              startIcon={isUpdating ? <CircularProgress size={20} /> : <CloudUpload />}
            >
              {isUpdating ? 'Đang cập nhật...' : 'Cập nhật giáo án'}
            </Button>
          </Box>
        </Box>

        {isUpdating && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Đang xử lý file và cập nhật thông tin...
            </Typography>
          </Box>
        )}
      </form>
    </Box>
  );
};

